import type { GTMEvent, GTMEventName } from 'types/gtm'
import { getActiveSplitTests } from '~/configuration/split-testing.configuration'

export const PosthogKeyEvents: Partial<GTMEventName>[] = [
  'general',
  'purchase',
]

function isKeyEvent(eventName: GTMEventName): boolean {
  return PosthogKeyEvents.includes(eventName)
}

const mapPosthogEventNames: Partial<Record<GTMEventName, string>> = {
  general: '$pageview',
}

function getPosthogEventName(eventName: GTMEventName): string {
  return mapPosthogEventNames[eventName] || eventName
}

/**
 * Determines if an event is part of a split test based on the active split tests
 * and the event's GTM event name.
 * @param event The GTM event to check
 * @returns Boolean indicating if the event is part of a split test
 */
function isEventInSplitTest(event: GTMEvent): boolean {
  const storefrontStore = useStorefrontStore()
  const storefront = storefrontStore.currentStorefrontCode
  const activeSplitTests = getActiveSplitTests(storefront)

  if (!activeSplitTests.length) {
    return false
  }

  const eventName = event.event as GTMEventName

  if (isKeyEvent(eventName)) {
    return true
  }

  const isSplitTestEvent = activeSplitTests.some((test) => {
    const testEventNames = test.gtmEventNames || []
    return testEventNames.includes(eventName)
  })

  if (isSplitTestEvent) {
    return true
  }

  return false
}

export function usePostHog() {
  const config = useRuntimeConfig()
  const isEnabled = computed(() => !!config.public.posthogApiKey && config.public.posthogEnabled)

  const trackEvent = (event: GTMEvent) => {
    if (!isEnabled.value)
      return

    // Only send the event if it's part of a split test
    if (!isEventInSplitTest(event))
      return

    const posthogClient = useNuxtApp().$posthog()
    const posthogEventName = getPosthogEventName(event.event as GTMEventName)
    posthogClient?.capture(posthogEventName, event)
  }

  return {
    trackEvent,
    isEventInSplitTest,
    isEnabled,
  }
}
